import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import {
  ModalController,
  Platform,
  PopoverController,
  ToastController,
} from "@ionic/angular";

import { TooltipDirective } from "@cloudfactorydk/ng2-tooltip-directive";
import { CookieService } from "ngx-cookie-service";
import { AnimationOptions } from 'ngx-lottie';
import { Finishing, PaperWeight, SettingCategoryText, Settings } from 'src/app/shared-lib/core/settings';
import { ProductService } from 'src/app/shared-lib/domains/product';
import { GroupedBindingSelectorComponent } from "../../components/grouped-binding-selector/grouped-binding-selector.component";
import { IncompatibleLaminationAlertComponent } from "../../components/incompatible-lamination-alert/incompatible-lamination-alert.component";
import { AppService } from "../../shared/app.service";
import { CartService } from "../../shared/cart.service";
import { TooltipInfoColorComponent } from "../tooltip-info-components/tooltip-info-color/tooltip-info-color.component";

@Component({
  selector: "app-options-button",
  templateUrl: "./options-button.component.html",
  styleUrls: ["./options-button.component.scss"],
  standalone: false
})
export class OptionsButtonComponent implements OnInit, OnDestroy {
  @Input() settings;
  @Input() color = "light";
  @Input() settingOption;
  @Input() hasImage;
  @Input() selectorType = "buttons";
  @Output() optionSelect = new EventEmitter<any>();
  @Output() settingSelectedEvent = new EventEmitter();
  @ViewChild("container", { read: ElementRef, static: false })
  containerRef: ElementRef;
  @ViewChildren("warningTooltip") warningTooltips: QueryList<TooltipDirective>;

  infoTip = null;
  infoCoord = {
    screenX: 0,
    screenY: 0,
  };
  infoTimeout;
  isOpened: boolean = null;
  isMobile: boolean = null;

  printProduct;
  toastShown = false;
  private lottieOptionsByPath: Record<string, AnimationOptions> = {};

  constructor(
    public popoverController: PopoverController,
    private modalController: ModalController,
    private cartService: CartService,
    private toastController: ToastController,
    private platform: Platform,
    private cookieService: CookieService,
    private productService: ProductService,
    private toastCtrl: ToastController,
    protected appService: AppService
  ) { }

  ngOnInit() {
    this.checkDevice();
    this.cartService.activePrintItem$.subscribe((printProduct) => {
      this.printProduct = printProduct;
    });
    if (this.settings[this.settingOption].categorized) {
      this.setCategorizedOptions(this.settings[this.settingOption].options);
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.settings && changes.settings.currentValue) ||
      (changes.settingOption && changes.settingOption.currentValue)
    ) {
      if (
        this.settings &&
        this.settingOption &&
        this.settings[this.settingOption] &&
        this.settings[this.settingOption].categorized
      ) {
        this.setCategorizedOptions(this.settings[this.settingOption].options);
      }
    }
  }

  ngAfterViewInit() {
    this.fixContainerHeight();
  }

  ngAfterViewChecked() {
    this.warningTooltips.forEach((t) => this.appService.registerTooltip(t));
  }

  ngOnDestroy() {
    this.warningTooltips.forEach((t) => this.appService.unregisterTooltip(t));
  }

  /**
   * Centra el tooltip (y su flecha) sobre el botón al hacer hover.
   *
   * La librería de tooltips calcula la posición mezclando getBoundingClientRect()
   * (que SÍ incluye el `zoom: 0.75` del parche global) con offsetWidth (que NO lo
   * incluye), por lo que la flecha queda desplazada. Aquí calculamos el centro
   * visual real del elemento con getBoundingClientRect() y se lo pasamos como
   * `position` explícita; al hacerlo, la librería ignora ese cálculo defectuoso.
   *
   * Se ejecuta de forma síncrona en el mouseenter, antes de que la librería
   * monte el tooltip (lo hace en un setTimeout), así la posición ya está lista.
   */
  centerTooltip(tooltip: TooltipDirective, event: MouseEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    tooltip.options = {
      ...tooltip.options,
      position: {
        top: rect.top,
        left: rect.left + rect.width / 2,
      },
    };
  }

  getLottieOptions(path: string): AnimationOptions {
    if (!path) {
      return null;
    }

    if (!this.lottieOptionsByPath[path]) {
      this.lottieOptionsByPath[path] = {
        path,
        autoplay: true,
        loop: true,
      };
    }

    return this.lottieOptionsByPath[path];
  }

  private checkDevice() {
    let platforms = this.platform.platforms();
    this.isMobile = !!platforms.find((type) => type == "mobile");
  }

  async selectOption(
    i: number,
    event: MouseEvent | null = null
  ): Promise<void> {
    const selectedOption = this.settings[this.settingOption].options[i];
    const isSelected = this.settings[this.settingOption].selected === i + 1;

    if (isSelected) return;
    if (
      [Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(
        selectedOption.value
      ) &&
      !this.cookieService.get("bindingSelected")
    ) {
      await this.handleBindingSelection(event, i);
    } else if (
      this.settingOption === Settings.FINISHING &&
      [Finishing.LAMINATED, Finishing.BINDING_LAMINATED].includes(
        selectedOption.value
      )
    ) {
      await this.handleLaminationSelection(i);
    } else {
      this.updateSelectedOption(i);
    }
  }

  private async handleBindingSelection(
    event: MouseEvent | null,
    i: number
  ): Promise<void> {
    const button = event?.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const popover = await this.popoverController.create({
      component: GroupedBindingSelectorComponent,
      cssClass: this.isMobile
        ? "groupedBindingSelectorPopoverMobile"
        : "groupedBindingSelectorPopover",
      event: { target: { getBoundingClientRect: () => rect } } as any,
      alignment: "center",
      side: this.isMobile ? "left" : "right",
      componentProps: {
        settings: this.settings,
        printProduct: this.printProduct,
      },
    });

    popover.onDidDismiss().then(async (result) => {
      if (result.data) {
        const canProceed = await this.handleLaminationSelection(i, false);
        if (!canProceed) {
          return;
        }
        this.cookieService.set("bindingSelected", "1");
        this.updateGroupedSetting(result.data.grouped);
        this.updateSelectedOption(i);
      }
    });

    //FIXME: comparar este fragmento con el de arriba
    // popover.onDidDismiss().then(async (result) => {
    //   if (result.data) {
    //     const acceptLamination = await this.handleLaminationSelection(i);
    //     if (acceptLamination) {
    //       this.cookieService.set('bindingSelected', '1');
    //       this.updateGroupedSetting(result.data.grouped);
    //       this.updateSelectedOption(i);
    //     }
    //   }
    // });

    await popover.present();
  }

  private async handleLaminationSelection(
    i: number,
    autoUpdate: boolean = true
  ): Promise<boolean> {
    const printGroupFiles = this.printProduct.product.printingGroup.files;
    const selectedPaperWeights = new Set(
      printGroupFiles
        .map((file) => file.coverType.paperWeight)
        .concat(
          this.settings[Settings.PAPER_WEIGHT].options[
            this.settings[Settings.PAPER_WEIGHT].selected - 1
          ].value
        )
    );
    const incompatibleFinishings = Array.from(selectedPaperWeights).flatMap(
      (paperWeight) => {
        const option = this.settings.paperWeight.options.find(
          (o) => o.value === paperWeight
        );
        // Si no existe la opción o no tiene restricciones, devolvemos array vacío
        if (!option?.unavailableFor) return [];

        // Esto nos da un array con TODOS los objetos que tengan id === "finishing"
        const finishingRestrictions = option.unavailableFor.filter(
          (unavailable) => unavailable.id === "finishing"
        );
        // Aplanamos los arrays de 'options' que haya dentro de cada restricción
        return finishingRestrictions.flatMap(
          (restriction) => restriction.options
        );
      }
    );

    if (incompatibleFinishings.includes(i + 1)) {
      const modal = await this.modalController.create({
        component: IncompatibleLaminationAlertComponent,
        cssClass: "auto-height",
      });
      await modal.present();

      const { data } = await modal.onDidDismiss();

      if (data) {
        this.productService.restartPaperWeight(
          this.printProduct,
          PaperWeight.W80,
          this.settings,
          this.optionSelect
        );
        if (autoUpdate) {
          this.updateSelectedOption(i);
        }
        const toast = await this.toastController.create({
          icon: "checkmark-circle-outline",
          message: "Gramaje del papel actualizado con éxito",
          color: "success",
          position: "bottom",
          animated: true,
          cssClass: "fitWidthToast successToast",
          duration: 3000,
        });
        await toast.present();
        setTimeout(() => {
          const triggerElement = document.getElementById(
            "tooltip-icon-lamination-weight-setting"
          );
          if (triggerElement) {
            triggerElement.click(); // Simula el clic en el trigger asociado
          }
        }, 500);
        return true;
      } else {
        return false;
      }
    } else {
      if (autoUpdate) {
        this.updateSelectedOption(i);
      }
      return true;
    }
  }

  //FIXME:
  // private async handleLaminationSelection(i) {
  //   return new Promise(async (resolve) => {
  //     const printGroupFiles = this.printProduct.product.printingGroup.files;
  //     const selectedPaperWeights = new Set(
  //       printGroupFiles.map(file => file.coverType.paperWeight)
  //         .concat(this.settings[Settings.PAPER_WEIGHT].options[this.settings[Settings.PAPER_WEIGHT].selected - 1].value)
  //     );
  //
  //     const incompatibleFinishings = Array.from(selectedPaperWeights).flatMap(paperWeight =>
  //       this.settings.paperWeight.options.find(option => option.value === paperWeight)?.unavailableFor?.find(unavailable => unavailable.id === 'finishing')?.options || []
  //     );
  //
  //     if (incompatibleFinishings.includes(i + 1)) {
  //       const modal = await this.modalController.create({
  //         component: IncompatibleLaminationAlertComponent,
  //         cssClass: 'auto-height',
  //       });
  //
  //       modal.onDidDismiss().then(async (result) => {
  //         if (result.data) {
  //           this.productService.restartPaperWeight(this.printProduct, PaperWeight.W80, this.settings, this.optionSelect);
  //           this.updateSelectedOption(i);
  //           const toast = await this.toastController.create({
  //             icon: 'checkmark-circle-outline',
  //             message: 'Gramaje del papel actualizado con éxito',
  //             color: "success",
  //             position: 'bottom',
  //             animated: true,
  //             cssClass: "fitWidthToast successToast",
  //             duration: 3000,
  //           });
  //           await toast.present();
  //           setTimeout(() => {
  //             const triggerElement = document.getElementById('tooltip-icon-lamination-weight-setting');
  //             if (triggerElement) {
  //               triggerElement.click(); // Simula el clic en el trigger asociado
  //             }
  //           }, 500)
  //           resolve(true);
  //         } else {
  //           resolve(false);
  //         }
  //       });
  //       await modal.present();
  //     } else {
  //       this.updateSelectedOption(i);
  //       resolve(true);
  //     }
  //   });
  // }

  private updateGroupedSetting(grouped: string): void {
    const selectedIndex =
      this.settings[Settings.GROUPED].options.findIndex(
        (option) => option.value === grouped
      ) + 1;
    this.settings[Settings.GROUPED].selected = selectedIndex;
    this.optionSelect.emit({
      setting: Settings.GROUPED,
      option: selectedIndex,
      prev: this.settings[Settings.GROUPED].selected,
    });
  }

  private updateSelectedOption(i: number): void {
    const prev = this.settings[this.settingOption].selected;
    this.settings[this.settingOption].selected = i + 1;
    this.optionSelect.emit({
      setting: this.settingOption,
      option: this.settings[this.settingOption].selected,
      prev: prev,
    });
  }

  selectOptionCheck(event) {
    const checked = event.detail.checked;
    this.selectOption(checked ? 0 : 1);
  }

  isDisabled(i) {
    const option = this.settings[this.settingOption].options[i];

    // Desactiva el botón de agrupado si hay un solo archivo en la carpeta
    if (
      this.settingOption === "grouped" &&
      option?.value === true &&
      this.printProduct?.product?.printingGroup?.files?.length === 1
    ) {
      // Si el botón de agrupado está seleccionado, cambia a individual
      if (this.settings[this.settingOption].selected === i + 1) {
        setTimeout(() => {
          const individualIndex = this.settings[
            this.settingOption
          ].options.findIndex((opt) => opt.value === false);
          if (individualIndex !== -1) {
            this.updateSelectedOption(individualIndex);
          }
        }, 0);
      }
      return true;
    }

    let isAvailable = this.checkAvailability(i);
    if (!isAvailable && this.settings[this.settingOption].selected === i + 1)
      this.selectAnother();
    return !isAvailable;
  }

  isHidden(i) {
    let hideSubtitleFor =
      this.settings[this.settingOption].options[i].hideSubtitleFor || [];
    return (
      -1 !==
      hideSubtitleFor.findIndex((hidden) => {
        return hidden.options.includes(this.settings[hidden.id].selected);
      })
    );
  }

  selectAnother() {
    //buscar el primer disponible para seleccionar y sino -1
    const available = this.settings[this.settingOption].options.findIndex(
      (ele, index) => this.checkAvailability(index)
    );
    let setting = this.settings[this.settingOption];
    //TODO: Verificar porque se ejecuta dos veces
    setTimeout(() => {
      this.optionSelect.emit({
        setting: this.settingOption,
        option: available + 1,
        prev: this.settings[this.settingOption].selected,
      });
      setting.selected = available + 1;
    }, 10);
  }

  checkAvailability(i) {
    const option = this.settings[this.settingOption].options[i];
    const isSelected = this.settings[this.settingOption].selected === i + 1;

    if (
      option.title === "Grapado" &&
      (this.isSomeCoverNotDefault() || this.isStapplingExceeded())
    ) {
      if (isSelected && !this.toastShown) {
        let message = "";

        if (this.isStapplingExceeded()) {
          message =
            "Se ha desactivado el grapado ya que la impresión de algún documento excede los 50 folios";
        } else if (this.isSomeCoverNotDefault()) {
          message =
            "Se ha desactivado el grapado ya que no está disponible para configuraciones de portada distintas a la predeterminada";
        }

        if (message) {
          this.toastShown = true;
          this.toastController
            .create({
              message: message,
              color: "danger",
              position: "bottom",
              cssClass: "fitWidthToast",
              duration: 5000,
            })
            .then((toast) => {
              toast.present();

              setTimeout(() => {
                this.toastShown = false;
              }, 3000);
            });
        }
      }
      return false;
    }
    const unavailableFor = option.unavailableFor || [];
    const unavailableForGrouped = option.unavailableForGrouped || [];

    const isUnavailable = unavailableFor.some((unavailable) =>
      unavailable.options.includes(this.settings[unavailable.id].selected)
    );

    const isUnavailableGrouped = unavailableForGrouped.some((group) =>
      group.properties.every((unavailable) =>
        unavailable.options.includes(this.settings[unavailable.id].selected)
      )
    );

    return !isUnavailable && !isUnavailableGrouped;
  }

  async clickInfo(event: MouseEvent, type = "mouseover") {
    const popover = await this.popoverController.create({
      component: TooltipInfoColorComponent,
    });

    await popover.present();

    const { role } = await popover.onDidDismiss();
    console.log(`Popover dismissed with role: ${role}`);
  }

  public elementScrollTop(id: string): void {
    const element: HTMLElement = document.getElementById(id);
    // element.scrollTo(0,0);
    element.scrollIntoView();
  }

  public buttonSubtitle(option, i) {
    return option?.subTitle ?? "";
    //los mensajes que aparecian como subtitulos ahora aparecen como tooltips
    const unavailableForIndex = option.unavailableFor
      ? option.unavailableFor.findIndex((unavailable) => {
        return unavailable.options.includes(
          this.settings[unavailable.id].selected
        );
      })
      : -1;
    let altSubtitle = null;

    if (this.settings[this.settingOption].options[i].title === "Grapado") {
      if (this.isStapplingExceeded()) {
        altSubtitle = "Límite excedido";
      }

      if (this.isSomeCoverNotDefault()) {
        altSubtitle = "Sólo tipo de portada por defecto";
      }
    }

    if (unavailableForIndex > -1) {
      altSubtitle = option.unavailableFor[unavailableForIndex].subTitle ?? null;
      // console.log("Availability", unavailableForIndex);
    }
    return altSubtitle ?? option?.subTitle;
  }

  private isStapplingExceeded() {
    return this.printProduct.product.printingGroup.files.some(
      (fil) => fil.printSheets >= 50
    );
  }

  private isSomeCoverColor() {
    return this.printProduct.product.printingGroup.files.some(
      (fil) => fil.coverColor
    );
  }

  private isSomeCoverNotDefault() {
    return this.printProduct.product.printingGroup.files.some(
      (fil) =>
        !fil.coverType?.default ||
        fil?.coverType?.paperWeight !==
        this.settings[Settings.PAPER_WEIGHT].options[
          this.settings[Settings.PAPER_WEIGHT].selected - 1
        ].value
    );
  }

  private isSomeCoverLaminated() {
    return this.printProduct.product.printingGroup.files.some(
      (fil) => fil.coverLaminated
    );
  }

  private fixContainerHeight() {

  }

  protected readonly Finishing = Finishing;

  protected readonly Settings = Settings;

  shouldShowCategory(options: any, i: number) {
    return (
      i === 0 ||
      (options[i - 1] &&
        options[i - 1].settingCategory !== options[i].settingCategory)
    );
  }

  getSettingCategoryText(option: any) {
    return SettingCategoryText[option.settingCategory];
  }

  getTooltip(option, i) {
    let tooltip = null;
    const unavailableForIndex = option.unavailableFor
      ? option.unavailableFor.findIndex((unavailable) => {
        return unavailable.options.includes(
          this.settings[unavailable.id].selected
        );
      })
      : -1;
    if (unavailableForIndex > -1) {
      tooltip =
        option.unavailableFor[unavailableForIndex].disabledTooltip ?? null;
      if (!!tooltip) {
        return tooltip;
      }
    }

    const unavailableForGroupedIndex = option.unavailableForGrouped
      ? option.unavailableForGrouped.findIndex((group) => {
        return group.properties.every((unavailable) => {
          return unavailable.options.includes(
            this.settings[unavailable.id].selected
          );
        });
      })
      : -1;

    if (unavailableForGroupedIndex > -1) {
      tooltip =
        option.unavailableForGrouped[unavailableForGroupedIndex]
          .disabledTooltip ?? tooltip;
    }
    if (this.settings[this.settingOption].options[i].title === "Grapado") {
      if (this.isStapplingExceeded()) {
        tooltip = "Límite de folios excedido";
      }

      if (this.isSomeCoverNotDefault()) {
        tooltip = "Sólo tipo de portada por defecto";
      }
    }

    // Si la opción está disponible (sin motivo de desactivación) y tiene un
    // tooltip informativo, lo mostramos con el mismo diseño (warning-tooltip).
    return tooltip ?? option.infoTooltip ?? null;
  }

  isBindingSelected(option: any): boolean {
    const finishing =
      this.printProduct.product.printingGroup.printSettings.finishing;
    const selectedOption = this.settings[this.settingOption].selected;
    const bindingIndex = this.appService.getSelectedIndexForOption(
      Settings.FINISHING,
      { value: "binding" }
    );
    const bindingWithCoverLaminatedIndex =
      this.appService.getSelectedIndexForOption(Settings.FINISHING, {
        value: "binding",
        extraData: { isBindingWithCoverLaminated: true },
      });
    const bindingLaminatedIndex = this.appService.getSelectedIndexForOption(
      Settings.FINISHING,
      { value: Finishing.BINDING_LAMINATED }
    );
    return (
      // CASO A: Encuadernado normal ("binding")
      (finishing === "binding" &&
        // 1. Encuadernado Simple: Debe coincidir el índice Y el valor debe ser "binding"
        ((selectedOption === bindingIndex &&
          option.value === "binding" && // <--- ESTO FALTABA
          !option?.extraData?.isBindingWithCoverLaminated) ||
          // 2. Encuadernado con Portada
          (selectedOption === bindingWithCoverLaminatedIndex &&
            option?.extraData?.isBindingWithCoverLaminated))) ||
      // CASO B: Encuadernado todo plastificado
      (selectedOption === bindingLaminatedIndex &&
        option.value === Finishing.BINDING_LAMINATED)
    );
  }

  setCategorizedOptions(options: any) {
    let index = 0;
    this.settings[this.settingOption].categorizedOptions = options.reduce(
      (acc, option) => {
        const lastCategory = acc[acc.length - 1];
        const optionWithIndex = { ...option, index: index++ };
        if (
          !lastCategory ||
          lastCategory.settingCategory !== option.settingCategory
        ) {
          acc.push({
            settingCategory: option.settingCategory,
            options: [optionWithIndex],
          });
        } else {
          lastCategory.options.push(optionWithIndex);
        }
        return acc;
      },
      []
    );
  }

  isSelected(i: number) {
    return this.settings[this.settingOption].selected === i + 1;
  }
}

